import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";

import { clientTestimonials } from "i18n/client";

import "swiper/css";
import "swiper/css/navigation";

const Testimonial = () => {
  const lang = useSelector((state) => state.language.lang);

  const t = clientTestimonials[lang];

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <section className="relative overflow-hidden bg-darkbg py-20 md:py-28">
      {/* Background Glow */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-reddark/5 blur-3xl rounded-full" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-reddark/5 blur-3xl rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          {/* <p className="uppercase tracking-[0.35em] text-sm text-reddark mb-4 font-medium">
            {t.smallTitle}
          </p> */}

          <p className="uppercase tracking-[0.3em] text-text text-xs md:text-sm mb-3">
            {t.smallTitle}
          </p>

          <h2 className="text-4xl md:text-6xl font-[Crimson] text-reddark leading-tight mb-3">
            {t.title}
          </h2>
          <p className="italic text-[#9b7b62] mb-3">{t.description}</p>
          <div className="w-24 h-[2px] bg-reddark mx-auto mt-8 mb-8" />
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-4 mb-12">
          <button
            ref={prevRef}
            className="
              w-12 h-12 rounded-full
              border border-reddark
              text-reddark
              flex items-center justify-center
              bg-white
              hover:bg-reddark
              hover:text-white
              transition-all duration-300
            "
          >
            <ChevronLeft size={20} />
          </button>

          <button
            ref={nextRef}
            className="
              w-12 h-12 rounded-full
              border border-reddark
              text-reddark
              flex items-center justify-center
              bg-white
              hover:bg-reddark
              hover:text-white
              transition-all duration-300
            "
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Swiper */}
        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={30}
          loop={true}
          speed={900}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          breakpoints={{
            0: {
              slidesPerView: 1,
            },

            768: {
              slidesPerView: 2,
            },

            1200: {
              slidesPerView: 3,
            },
          }}
          className="!overflow-visible"
        >
          {t.testimonials.map((item, index) => (
            <SwiperSlide key={index} className="h-auto">
              {/* <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                }}
                viewport={{ once: true }}
                className="
                  group relative h-full
                  rounded-[2.2rem]
                  bg-white/90
                  backdrop-blur-xl
                  border border-[#ece7e2]
                  p-8 md:p-10
                  overflow-hidden
                  transition-all duration-500
                  hover:-translate-y-3
                  hover:shadow-[0_25px_80px_rgba(0,0,0,0.08)]
                "
              >
                <div
                  className="
        bg-reddark
        text-white
        text-center
        text-sm
        font-bold
        rounded-lg
        py-2
        uppercase
      "
                >
                  {item.category}
                </div>

                <div
                  className="
        mt-3
        bg-white
        rounded-lg
        border
        border-[#d8d0c7]
        text-center
        py-4
      "
                >
                  <div className="text-5xl font-bold text-reddark">
                    {item.result}
                  </div>

                  <div className="text-gray-600 text-sm mt-1">
                    {item.period}
                  </div>
                </div>

                <div className="flex justify-center gap-1 py-4 text-amber-500">
                  ★★★★★
                </div>
                <div className="absolute top-0 left-0 h-1.5 w-full bg-gradient-to-r from-reddark via-redlight to-reddark" />

                <div className="absolute -top-10 -right-10 w-40 h-40 bg-reddark/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition duration-700" />

                <div
                  className="
                    w-14 h-14 rounded-2xl
                    bg-reddark/10
                    flex items-center justify-center
                    mb-8
                  "
                >
                  <Quote className="w-7 h-7 text-reddark" />
                </div>

                <div className="space-y-5">
                  {item.content.map((text, i) => (
                    <p
                      key={i}
                      className="
                        text-[#222]
                        text-[15px] md:text-base
                        leading-relaxed
                      "
                    >
                      {text}
                    </p>
                  ))}
                </div>

                <div className="mt-10 pt-6 border-t border-black/10">
                  <h3 className="text-xl font-semibold text-[#111]">
                    {item.name}
                  </h3>

                  <p className="mt-1 text-sm uppercase tracking-[0.2em] text-reddark">
                    {item.role}
                  </p>
                </div>
              </motion.div> */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                }}
                viewport={{ once: true }}
                className="
    group relative h-full
    rounded-[2.2rem]
    bg-white/90
    backdrop-blur-xl
    border border-[#ece7e2]
    p-8 md:p-10
    overflow-hidden
    transition-all duration-500
    hover:-translate-y-3
    hover:shadow-[0_25px_80px_rgba(0,0,0,0.08)]
  "
              >
                {/* Accent */}
                <div className="absolute top-0 left-0 h-1.5 w-full bg-gradient-to-r from-reddark via-redlight to-reddark" />

                {/* Glow */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-reddark/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition duration-700" />

                {/* Category */}
                <div className="flex justify-center mb-4">
                  <span
                    className="
        px-4 py-2
        rounded-full
        bg-reddark
        text-white
        text-xs
        font-semibold
        tracking-[0.2em]
        uppercase
      "
                  >
                    {item.category}
                  </span>
                </div>

                {/* Result */}
                <div
                  className="
      mb-8
      rounded-2xl
      border border-[#ece7e2]
      bg-gradient-to-b
      from-white
      to-[#faf7f3]
      p-6
      text-center
    "
                >
                  <div className="text-xl md:text-3xl font-bold text-reddark">
                    {item.result}
                  </div>

                  <div className="mt-2 text-sm uppercase tracking-[0.15em] text-[#8b7355]">
                    {item.period}
                  </div>

                  <div className="flex justify-center gap-1 mt-4 text-amber-500">
                    ★★★★★
                  </div>
                </div>

                {/* Quote Icon */}
                <div
                  className="
      w-14 h-14 rounded-2xl
      bg-reddark/10
      flex items-center justify-center
      mb-8
    "
                >
                  <Quote className="w-7 h-7 text-reddark" />
                </div>

                {/* Content */}

                <div className="space-y-5">
                  {item.content.map((text, i) => (
                    <p
                      key={i}
                      className="
                        text-[#222]
                        text-[15px] md:text-base
                        leading-relaxed
                      "
                    >
                      {text}
                    </p>
                  ))}
                </div>

                {/* Footer */}
                <div className="mt-10 pt-6 border-t border-black/10">
                  <h3 className="text-xl font-semibold text-[#111]">
                    {item.name}
                  </h3>

                  <p className="mt-1 text-sm uppercase tracking-[0.2em] text-reddark">
                    {item.role}
                  </p>
                </div>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default Testimonial;
